import { useEffect } from "react";
import { useParams } from "react-router";
import {
  Autocomplete,
  Chip,
  FormControl,
  ListItemText,
  type SxProps,
  TextField,
  type Theme,
} from "@mui/material";
import { useQueryEnvs } from "src/queries/env/env.query";
import { useGlobalLocalContext } from "src/storage/localStorage.ts";
import { CopyText } from "@components/features/small/CopyText/CopyText.tsx";

const MENU_MAX_HEIGHT = 500;

const DEFAULT_FORM_SX = { width: "100%" };

interface AssetsFilterEnvProps {
  env?: string;
  timeWindow?: string | number;
  setIsValidEnv?: (isValid: boolean) => void;
  onChange?: (value: string) => void;
  formSx?: SxProps<Theme>;
  className?: string;
}

const AssetsFilterEnv = ({
  env,
  setIsValidEnv,
  onChange,
  formSx = DEFAULT_FORM_SX,
  className,
}: AssetsFilterEnvProps) => {
  const { data: envs, isFetching, isFetched } = useQueryEnvs();
  const params = useParams();
  const { isDebugMode } = useGlobalLocalContext();

  env ??= params.env_name;

  const matched = env ? envs?.find((e) => e.env === env) : undefined;
  const value = matched ?? null;

  // make sure the `env` prop (derived from URL query-param) exists in the envs list
  useEffect(() => {
    if (isFetched && env) {
      setIsValidEnv?.(!!matched);
    }
  }, [isFetched, env, matched, setIsValidEnv]);

  return (
    <FormControl sx={formSx} size="small" className={className}>
      <Autocomplete
        loading={isFetching}
        value={value}
        onChange={(_e, newValue) => onChange?.(newValue?.env ?? "")}
        options={envs ?? []}
        disabled={isFetching || !envs?.length}
        size="small"
        // Must be dynamic — static `disableClearable` narrows MUI's value type to exclude `null`,
        // but `null` is needed to represent "no selection" (e.g. compare-runs form group2 before auto-fill)
        disableClearable={!!value}
        getOptionLabel={(option) => option.env}
        isOptionEqualToValue={(option, val) => option.env === val?.env}
        getOptionKey={(option) => option.env}
        renderInput={(params) => (
          <TextField
            {...params}
            label={
              <span className="flex">
                Env{isDebugMode && value?.env_id && <CopyText text={value.env_id} />}
              </span>
            }
          />
        )}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;

          return (
            <li {...optionProps} key={key} data-test-id={"env-select-" + option.env}>
              <ListItemText primary={option.env} />
              <Chip label={option.cnt} size="small" sx={{ ml: 3 }} />
            </li>
          );
        }}
        slotProps={{
          listbox: { sx: { maxHeight: MENU_MAX_HEIGHT } },
          paper: { sx: { width: "max-content", minWidth: "100%" } },
        }}
        data-test-id="app-filters--env-select"
      />
    </FormControl>
  );
};

export default AssetsFilterEnv;
